"use client";

import * as stylex from "@stylexjs/stylex";
import { ArrowRight, ChevronLeft, ChevronRight, type LucideIcon, Sun, Users, X } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { copy } from "@/copy";
import type { Breakpoint } from "@/domain/breakpoint";
import { type EventWord, nextWord } from "@/domain/eventSentence";
import { defaultEnd, isEndHourDisabled, isEndMinuteDisabled, type Meridiem, pickerHours, pickerMinutes, withHour, withMeridiem, withMinute } from "@/domain/eventTimes";
import { monthCells, monthOf, monthTitle, stepMonth } from "@/domain/quickDate";
import { EVENT_LIMITS, TAG_NAME_LIMIT, type Tag, type TagColor } from "@/domain/schemas";
import { suggestTags } from "@/domain/tagMatch";
import { exactTag, nextFreeColor, pickWho } from "@/domain/tags";
import { type Clock, formatDayName, formatHour, formatLength } from "@/domain/time";
import { parseDateKey } from "@/domain/zone";
import "@/theme/seasons.stylex";
import { season } from "@/theme/seasons.stylex";
import "@/theme/tokens.stylex";
import { colors, motion, radii } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "@/ui/Button";
import { Eyebrow } from "@/ui/Eyebrow";
import { Icon } from "@/ui/Icon";
import { MeridiemToggle } from "@/ui/MeridiemToggle";
import { PersonDot } from "@/ui/PersonDot";
import { press } from "@/ui/press";
import type { EventPageAction, PageDraft } from "../eventPage";
import { NewMember, Suggestions } from "./WhoField";
type EventPickerProps = {
  breakpoint: Breakpoint;
  word: EventWord;
  draft: PageDraft;
  today: string;
  clock: Clock;
  tags: readonly Tag[];
  recentTitles: readonly string[];
  recentPlaces: readonly string[];
  onAction: (action: EventPageAction) => void;
  onCreateTag: (name: string, color: TagColor) => string;
};
type BodyProps = EventPickerProps;
type ChipProps = {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
  xstyle?: stylex.StyleXStyles;
};
type CellProps = {
  label: string;
  name: string;
  selected: boolean;
  disabled: boolean;
  breakpoint: Breakpoint;
  onClick: () => void;
};
type RowProps = {
  title: string;
  breakpoint: Breakpoint;
  children: ReactNode;
  cells?: boolean;
};
type ArrowProps = {
  icon: LucideIcon;
  label: string;
  disabled?: boolean;
  onClick: () => void;
};
type TextField = HTMLInputElement | HTMLTextAreaElement;
const words = copy.events.picker;
const fields = copy.events.page.fields;
const styles = {
  card: {
    kzQI83: "x1iyjqo2",
    kmuXW: "x2lah0s",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1c1vhfx",
    kaIpWk: "xwe7p7i",
    kWkggS: "xh540kj",
    kMwMTN: "x1a11ngh",
    $$css: true
  },
  eyebrow: {
    kmuXW: "x2lah0s",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  soft: {
    kWkggS: "x5r8ks9",
    $$css: true
  },
  meridiem: {
    kmuXW: "x2lah0s",
    kzqmXN: "x2pejg6",
    kWkggS: "x5r8ks9",
    kGVxlE: "x1gnnqk1",
    $$css: true
  },
  allDay: {
    kZKoxP: "xn3w4p2",
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "x167g77z",
    k8WAf4: "xt970qd",
    kZCmMZ: "x1onr9mi",
    kwRFfy: "x1xnnf8n",
    kMzoRj: "xc342km",
    kaIpWk: "xqhrb7l",
    kWkggS: "x5r8ks9",
    kMwMTN: "x1a11ngh",
    kMv6JI: "xjb2p0i",
    kGuDYH: "x1j61zf2",
    k63SB2: "x1cf7cre",
    khDVqt: "xuxw1ft",
    kkrTdU: "x1ypdohk",
    k1ekBW: "xk9q25i",
    kIyJzY: "x16422bf",
    $$css: true
  },
  allDayOn: {
    kWkggS: "xfenp3h",
    kMwMTN: "xnnvkky",
    k63SB2: "xfbjfs5",
    $$css: true
  },
  input: {
    kmuXW: "x2lah0s",
    kzqmXN: "xh8yej3",
    kB7OPa: "x9f619",
    k8WAf4: "xxlmvz2",
    kg3NbH: "x17qtykl",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x1ky62hi x4ac898",
    kaIpWk: "x1fv5t7x",
    kGVxlE: "x1gnnqk1 xisfnwr",
    kI3sdo: "x1a2a7pz",
    kHenm0: "xtt52l0",
    kWkggS: "xh540kj",
    kMwMTN: "x1a11ngh",
    kMv6JI: "x17jlq2w",
    k63SB2: "xo1l8bm",
    kb6lSQ: "x1tup8yo",
    kLWn49: "xwn7fz2",
    $$css: true
  },
  chip: {
    kAzted: "x1wxaq2x",
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "x883omv",
    k8WAf4: "xt970qd",
    kg3NbH: "x17qtykl",
    kMzoRj: "xc342km",
    kaIpWk: "xqhrb7l",
    kWkggS: "x5r8ks9",
    kMwMTN: "x1a11ngh",
    kMv6JI: "xjb2p0i",
    kGuDYH: "x1j61zf2",
    k63SB2: "x1cf7cre",
    kkrTdU: "x1ypdohk",
    k1ekBW: "xk9q25i",
    kIyJzY: "x1gozvy9",
    $$css: true
  },
  picked: {
    kWkggS: "xfenp3h",
    kMwMTN: "xnnvkky",
    k63SB2: "xfbjfs5",
    $$css: true
  },
  person: {
    kAzted: "x1wiwyrm",
    kZCmMZ: "xf7dkkf",
    kwRFfy: "x1o5r3ls",
    kGuDYH: "x19d36u7",
    $$css: true
  },
  arrow: {
    kzqmXN: "x187nhsf",
    kZKoxP: "xn3w4p2",
    k1xSpc: "xrvj5dj",
    kgQiWS: "x1ku5rj1",
    kmVPX3: "x1717udv",
    kMzoRj: "xc342km",
    kaIpWk: "xqhrb7l",
    kWkggS: "xjbqb8w",
    kMwMTN: "x1a11ngh x1r7td6i",
    kkrTdU: "x1ypdohk xtgyt42",
    $$css: true
  },
  row: {
    k1xSpc: "xrvj5dj",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1fkiil2",
    $$css: true
  },
  cells: {
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    k7Eaqz: "xeuugli",
    kMzoRj: "xc342km",
    k1xSpc: "xrvj5dj",
    kOIVth: "x17d4w8g",
    $$css: true
  },
  lengths: {
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    k7Eaqz: "xeuugli",
    kMzoRj: "xc342km",
    k1xSpc: "x78zum5",
    kwnvtZ: "x1a02dak",
    kOIVth: "x17d4w8g",
    $$css: true
  },
  cell: {
    k7Eaqz: "xeuugli",
    kmVPX3: "x1717udv",
    kMzoRj: "xc342km",
    kaIpWk: "xqhrb7l",
    kWkggS: "x5r8ks9",
    kMwMTN: "x1a11ngh",
    kMv6JI: "xjb2p0i",
    kGuDYH: "x19d36u7",
    k63SB2: "xo1l8bm",
    kcqcaj: "xss6m8b",
    kkrTdU: "x1ypdohk",
    k1ekBW: "xk9q25i",
    kIyJzY: "x1gozvy9",
    $$css: true
  },
  cellOff: {
    kWkggS: "xjbqb8w",
    kMwMTN: "x19r2jmw",
    kkrTdU: "xt0e3qv",
    $$css: true
  },
  lengthCell: {
    kg3NbH: "x17qtykl",
    kGuDYH: "x1j61zf2",
    khDVqt: "xuxw1ft",
    $$css: true
  }
};
const cardSizes = {
  large: {
    kLKAdn: "x7sb2j6",
    kg3NbH: "x1sdxjpb",
    kGO01o: "xq1608w",
    $$css: true
  },
  normal: {
    kLKAdn: "x1p5oq8j",
    kg3NbH: "xj7an9n",
    kGO01o: "x1gan7if",
    $$css: true
  },
  small: {
    kmVPX3: "x1qhigcl",
    $$css: true
  }
};
const inputSizes = {
  large: {
    kAzted: "x1qafhyn",
    kGuDYH: "x1s3cmhv",
    $$css: true
  },
  normal: {
    kAzted: "x8nclml",
    kGuDYH: "xneq65o",
    $$css: true
  },
  small: {
    kAzted: "xbktkl8",
    kGuDYH: "x1i1m3gp",
    $$css: true
  }
};
const rowSizes = {
  large: {
    kumcoG: "xywn1d4",
    $$css: true
  },
  normal: {
    kumcoG: "xywn1d4",
    $$css: true
  },
  small: {
    kumcoG: "x1mkdm3x",
    $$css: true
  }
};
const cellsSizes = {
  large: {
    kumcoG: "x7lthxy",
    $$css: true
  },
  normal: {
    kumcoG: "x7lthxy",
    $$css: true
  },
  small: {
    kumcoG: "x1jl941j",
    $$css: true
  }
};
const cellHeights = {
  large: {
    kZKoxP: "xnnlda6",
    $$css: true
  },
  normal: {
    kZKoxP: "xdd8jsf",
    $$css: true
  },
  small: {
    kZKoxP: "xn3w4p2",
    $$css: true
  }
};
export function EventPicker(props: EventPickerProps) {
  const {
    breakpoint,
    word
  } = props;
  const label = headerLabel(props);
  return <section aria-label={label} {...stylex.props(styles.card, cardSizes[breakpoint])}>
      <Header {...props} label={label} />
      {word === "title" && <TitleBody {...props} />}
      {word === "day" && <DayBody {...props} />}
      {(word === "start" || word === "end") && <TimeBody {...props} />}
      {word === "who" && <WhoBody {...props} />}
      {word === "where" && <WhereBody {...props} />}
      {word === "notes" && <NotesBody {...props} />}
    </section>;
}
function Header({
  breakpoint,
  word,
  draft,
  clock,
  onAction,
  label
}: BodyProps & {
  label: string;
}) {
  const next = nextWord(word, {
    allDay: draft.allDay,
    hasEnd: draft.end !== undefined
  });
  const timed = word === "start" || word === "end";
  const value = word === "end" ? draft.end ?? defaultEnd(draft.start) : draft.start;
  const off = word === "start" && draft.allDay === true;
  const pick = (minutes: number) => onAction({
    type: word === "end" ? "end" : "start",
    value: minutes
  });
  return <div className="x2lah0s x1gg8mnh x78zum5 x1a02dak x6s0dn4 x1fkiil2">
      {word === "day" ? <RepeatTabs weekly={draft.repeat === "weekly"} onAction={onAction} /> : <Eyebrow xstyle={styles.eyebrow}>{label}</Eyebrow>}
      {timed && clock === "12h" && <MeridiemToggle label={words.meridiem} value={meridiemOf(value)} disabled={off} onChange={(meridiem: Meridiem) => pick(withMeridiem(value, meridiem))} xstyle={styles.meridiem} />}
      {word === "start" && <AllDayChip on={off} onAction={onAction} />}
      <span className="x1iyjqo2" />
      {next && <Button variant="ghost" iconRight={ArrowRight} onClick={() => onAction({
      type: "next"
    })} xstyle={styles.soft}>
          {breakpoint === "small" ? words.next : words.nextTo(fields[next])}
        </Button>}
    </div>;
}
function AllDayChip({
  on,
  onAction
}: {
  on: boolean;
  onAction: EventPickerProps["onAction"];
}) {
  return <button type="button" role="switch" aria-checked={on} onClick={() => onAction({
    type: "allDay",
    value: !on
  })} {...stylex.props(styles.allDay, on && styles.allDayOn, press.scale)}>
      <span {...{
      0: {
        className: "x78zum5 x1c071of x11xpdln xjmur4l x12w9bfk x1a4o8uh"
      },
      1: {
        className: "x78zum5 x11xpdln xjmur4l x12w9bfk x1a4o8uh x13ocgul"
      }
    }[!!on << 0]}>
        <Icon icon={Sun} size={18} />
      </span>
      {words.allDay}
    </button>;
}
function RepeatTabs({
  weekly,
  onAction
}: {
  weekly: boolean;
  onAction: EventPickerProps["onAction"];
}) {
  const tab = (on: boolean, name: string) => <button type="button" role="tab" aria-selected={weekly === on} onClick={() => onAction({
    type: "repeat",
    value: on
  })} {...{
    0: {
      className: "x1n2onr6 x1717udv xc342km x87fg2h xjbqb8w x1a11ngh xjb2p0i x1j61zf2 x1cf7cre xuxw1ft x1ypdohk xt3l3uh xpceaqw"
    },
    1: {
      className: "x1n2onr6 x1717udv xc342km x87fg2h xjbqb8w xjb2p0i x1j61zf2 xuxw1ft x1ypdohk xt3l3uh xpceaqw xnnvkky xfbjfs5"
    }
  }[!!(weekly === on) << 0]}>
      {name}
    </button>;
  return <div role="tablist" aria-label={words.howOften} className="x1n2onr6 x2lah0s xrvj5dj x1o61qjw x9f619 x1q5yig5 xn3w4p2 xfawy5m x87fg2h x5r8ks9">
      <span aria-hidden="true" {...{
      0: {
        className: "x10l6tqk xndqk7f xbfrwjf xcqyyxr x1gefphp x87fg2h xfenp3h x1c071of x11xpdln xpceaqw x12w9bfk x18l85"
      },
      1: {
        className: "x10l6tqk xndqk7f xbfrwjf xcqyyxr x1gefphp x87fg2h xfenp3h x11xpdln xpceaqw x12w9bfk x18l85 xumwmo6"
      }
    }[!!weekly << 0]} />
      {tab(false, words.oneDay)}
      {tab(true, words.everyWeek)}
    </div>;
}
function TitleBody({
  breakpoint,
  draft,
  recentTitles,
  onAction
}: BodyProps) {
  const ref = useFocusAtEnd<HTMLTextAreaElement>();
  const pickRecent = (title: string) => {
    onAction({
      type: "title",
      value: title
    });
    onAction({
      type: "select",
      word: "day"
    });
  };
  return <>
      <textarea ref={ref} aria-label={fields.title} value={draft.title} rows={2} maxLength={EVENT_LIMITS.title} placeholder={words.titlePlaceholder} onChange={event => onAction({
      type: "title",
      value: event.target.value.replace(/\n/g, " ")
    })} onKeyDown={enterIsNext(onAction)} {...stylex.props(styles.input, inputSizes[breakpoint])} />
      <Recent title={words.recent} values={recentTitles} current={draft.title} onPick={pickRecent} />
    </>;
}
function DayBody({
  draft,
  today,
  onAction
}: BodyProps) {
  const [month, setMonth] = useState(monthOf(draft.day));
  const pick = (day: string) => onAction({
    type: "day",
    value: day
  });
  const repeats = (day: string) => draft.repeat === "weekly" && isLaterSameWeekday(day, draft.day);
  return <div className="x17fpy1y xrvj5dj x1gj6vdr x6s0dn4 x1jnr06f">
      <span className="xgkxs2y x7v7x1q x78zum5">
        <MonthArrow icon={ChevronLeft} label={words.previousMonth} disabled={month <= monthOf(today)} onClick={() => setMonth(stepMonth(month, -1))} />
      </span>
      <span aria-live="polite" className="x1n57s4e x2b8uid x19d36u7 xfbjfs5 xuxw1ft">
        {monthTitle(month)}
      </span>
      <span className="x1xu9id6 x7v7x1q x78zum5">
        <MonthArrow icon={ChevronRight} label={words.nextMonth} onClick={() => setMonth(stepMonth(month, 1))} />
      </span>
      <span aria-hidden="true" className="x1osaytk xdk7pt" />
      {words.weekdays.map((letter, index) =>
    // biome-ignore lint/suspicious/noArrayIndexKey: the seven weekday letters repeat and never reorder
    <span key={index} aria-hidden="true" className="xr0iwhn xhunfcg xt3et7w xtvhhri xjkvuk6 x2b8uid">
          {letter}
        </span>)}
      {monthCells(month).map((day, index) => day === null ?
    // biome-ignore lint/suspicious/noArrayIndexKey: blank leading cells have no other identity
    <span key={index} /> : <button key={day} type="button" aria-label={words.dayCell(formatDayName(parseDateKey(day), "UTC"), day === today, repeats(day))} aria-pressed={day === draft.day} aria-current={day === today ? "date" : undefined} disabled={day < today} onClick={() => pick(day)} {...{
      0: {
        className: "xn3w4p2 xeuugli x1717udv xc342km xqhrb7l xjbqb8w x1a11ngh x1r7td6i xjb2p0i x1j61zf2 xo1l8bm xss6m8b x1ypdohk xtgyt42 x15406qy x16c7our"
      },
      4: {
        className: "xn3w4p2 xeuugli x1717udv xc342km xqhrb7l xjbqb8w x1a11ngh x1r7td6i xjb2p0i x1j61zf2 xss6m8b x1ypdohk xtgyt42 x15406qy x16c7our xuamy0h xfbjfs5"
      },
      2: {
        className: "xn3w4p2 xeuugli x1717udv xc342km xqhrb7l x1a11ngh x1r7td6i xjb2p0i x1j61zf2 xo1l8bm xss6m8b x1ypdohk xtgyt42 x15406qy x16c7our x1ubcl0s"
      },
      6: {
        className: "xn3w4p2 xeuugli x1717udv xc342km xqhrb7l x1a11ngh x1r7td6i xjb2p0i x1j61zf2 xss6m8b x1ypdohk xtgyt42 x15406qy x16c7our xuamy0h xfbjfs5 x1ubcl0s"
      },
      1: {
        className: "xn3w4p2 xeuugli x1717udv xc342km xqhrb7l xjb2p0i x1j61zf2 xss6m8b x1ypdohk xtgyt42 x15406qy x16c7our xfenp3h xnnvkky xfbjfs5"
      },
      5: {
        className: "xn3w4p2 xeuugli x1717udv xc342km xqhrb7l xjb2p0i x1j61zf2 xss6m8b x1ypdohk xtgyt42 x15406qy x16c7our xuamy0h xfenp3h xnnvkky xfbjfs5"
      },
      3: {
        className: "xn3w4p2 xeuugli x1717udv xc342km xqhrb7l xjb2p0i x1j61zf2 xss6m8b x1ypdohk xtgyt42 x15406qy x16c7our xfenp3h xnnvkky xfbjfs5"
      },
      7: {
        className: "xn3w4p2 xeuugli x1717udv xc342km xqhrb7l xjb2p0i x1j61zf2 xss6m8b x1ypdohk xtgyt42 x15406qy x16c7our xuamy0h xfenp3h xnnvkky xfbjfs5"
      }
    }[!!(day === today && day !== draft.day) << 2 | !!repeats(day) << 1 | !!(day === draft.day) << 0]}>
            {parseDateKey(day).getUTCDate()}
          </button>)}
    </div>;
}
function TimeBody({
  breakpoint,
  word,
  draft,
  clock,
  onAction
}: BodyProps) {
  const end = word === "end";
  const value = end ? draft.end ?? defaultEnd(draft.start) : draft.start;
  const off = !end && draft.allDay === true;
  const hour = Math.floor(value / 60);
  const minute = value % 60;
  const pick = (minutes: number) => onAction({
    type: end ? "end" : "start",
    value: minutes
  });
  return <div className="x78zum5 xdt5ytf xou54vl">
      <Row title={words.hour} breakpoint={breakpoint} cells>
        {pickerHours(clock, meridiemOf(value)).map(h => <Cell key={h} {...hourCell(h, clock)} breakpoint={breakpoint} selected={!off && h === hour} disabled={off || end && isEndHourDisabled(draft.start, h)} onClick={() => pick(withHour(value, h))} />)}
      </Row>
      <Row title={words.minutes} breakpoint={breakpoint} cells>
        {pickerMinutes(5).map(m => <Cell key={m} label={`:${pad(m)}`} name={words.minuteCell(pad(m))} breakpoint={breakpoint} selected={!off && m === minute} disabled={off || end && isEndMinuteDisabled(draft.start, hour, m)} onClick={() => pick(withMinute(value, m))} />)}
      </Row>
      {end && <Row title={words.length} breakpoint={breakpoint}>
          {words.lengths.map(length => <button key={length.value} type="button" aria-pressed={draft.end !== undefined && draft.end - draft.start === length.value} onClick={() => onAction({
        type: "length",
        minutes: length.value
      })} {...stylex.props(styles.cell, styles.lengthCell, cellHeights[breakpoint], draft.end !== undefined && draft.end - draft.start === length.value && styles.picked, press.scale)}>
              {length.label}
            </button>)}
          <Button variant="ghost" icon={X} onClick={() => onAction({
        type: "noEnd"
      })}>
            {words.noEnd}
          </Button>
        </Row>}
    </div>;
}
function WhoBody({
  draft,
  tags,
  onAction,
  onCreateTag
}: BodyProps) {
  const [input, setInput] = useState("");
  const [color, setColor] = useState<TagColor | null>(null);
  const allIds = tags.map(tag => tag.id);
  const everyone = allIds.every(id => draft.tagIds.includes(id));
  const name = input.trim();
  const exact = exactTag(tags, input);
  const add = (id: string) => {
    if (!draft.tagIds.includes(id)) onAction({
      type: "who",
      tagIds: [...draft.tagIds, id]
    });
    setInput("");
  };
  const create = () => {
    add(onCreateTag(name, color ?? nextFreeColor(tags)));
    setColor(null);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (exact) add(exact.id);else if (name !== "") create();
  };
  return <>
      <fieldset aria-label={fields.who} className="x1ghz6dp x1717udv xeuugli xc342km x78zum5 x1a02dak x167g77z">
        <Chip selected={everyone} onClick={() => onAction({
        type: "who",
        tagIds: pickWho(draft.tagIds, allIds)
      })} xstyle={styles.person}>
          <Icon icon={Users} size={18} />
          {copy.events.everyone}
        </Chip>
        {tags.map(tag => <Chip key={tag.id} selected={!everyone && draft.tagIds.includes(tag.id)} onClick={() => onAction({
        type: "who",
        tagIds: pickWho(draft.tagIds, allIds, tag.id)
      })} xstyle={styles.person}>
            <PersonDot color={tag.color} />
            {tag.name}
          </Chip>)}
      </fieldset>
      <input type="text" aria-label={words.addPerson} autoComplete="off" value={input} maxLength={TAG_NAME_LIMIT} placeholder={words.addPerson} onChange={event => setInput(event.target.value)} onKeyDown={onKeyDown} className="x2lah0s xh8yej3 x9f619 x17qtykl xmkeg23 x1y0btm7 x1ky62hi x4ac898 x1fv5t7x x1gnnqk1 xisfnwr x1a2a7pz xtt52l0 xh540kj x1a11ngh x17jlq2w xo1l8bm x1tup8yo xwn7fz2 xbktkl8 xt970qd x1n63bex" />
      {name !== "" && !exact && <NewMember name={name} color={color ?? nextFreeColor(tags)} onColor={setColor} onCreate={create} />}
      <Suggestions tags={suggestTags(tags, input, draft.tagIds)} onAdd={add} />
    </>;
}
function WhereBody({
  breakpoint,
  draft,
  recentPlaces,
  onAction
}: BodyProps) {
  const ref = useFocusAtEnd<HTMLInputElement>();
  return <>
      <input ref={ref} type="text" aria-label={fields.where} value={draft.location} maxLength={EVENT_LIMITS.location} placeholder={words.wherePlaceholder} onChange={event => onAction({
      type: "location",
      value: event.target.value
    })} onKeyDown={enterIsNext(onAction)} {...stylex.props(styles.input, inputSizes[breakpoint])} />
      <Recent title={words.recentPlaces} values={recentPlaces} current={draft.location} onPick={place => onAction({
      type: "location",
      value: place
    })} />
    </>;
}
function NotesBody({
  draft,
  onAction
}: BodyProps) {
  const ref = useFocusAtEnd<HTMLTextAreaElement>();
  return <textarea ref={ref} aria-label={fields.notes} value={draft.notes} rows={4} maxLength={EVENT_LIMITS.notes} placeholder={words.notesPlaceholder} onChange={event => onAction({
    type: "notes",
    value: event.target.value
  })} className="x2lah0s xh8yej3 x9f619 x17qtykl xmkeg23 x1y0btm7 x1ky62hi x4ac898 x1fv5t7x x1gnnqk1 xisfnwr x1a2a7pz xtt52l0 xh540kj x1a11ngh x17jlq2w xo1l8bm x1xn8qrt xwsyq91 x1i21sxh x37zpob" />;
}
function Recent({
  title,
  values,
  current,
  onPick
}: {
  title: string;
  values: readonly string[];
  current: string;
  onPick: (value: string) => void;
}) {
  if (values.length === 0) return null;
  return <div className="x78zum5 xdt5ytf x1v2ro7d">
      <Eyebrow>{title}</Eyebrow>
      <fieldset aria-label={title} className="x1ghz6dp x1717udv xeuugli xc342km x78zum5 x1a02dak x167g77z">
        {values.map(value => <Chip key={value} selected={current === value} onClick={() => onPick(value)}>
            {value}
          </Chip>)}
      </fieldset>
    </div>;
}
function Chip({
  selected,
  onClick,
  children,
  xstyle
}: ChipProps) {
  return <button type="button" aria-pressed={selected} onClick={onClick} {...stylex.props(styles.chip, xstyle, selected && styles.picked, press.scale)}>
      {children}
    </button>;
}
function Row({
  title,
  breakpoint,
  children,
  cells
}: RowProps) {
  return <div {...stylex.props(styles.row, rowSizes[breakpoint])}>
      <span aria-hidden="true" className="xr0iwhn xhunfcg xt3et7w xtvhhri">
        {title}
      </span>
      <fieldset aria-label={title} {...stylex.props(cells ? styles.cells : styles.lengths, cells && cellsSizes[breakpoint])}>
        {children}
      </fieldset>
    </div>;
}
function Cell({
  label,
  name,
  selected,
  disabled,
  breakpoint,
  onClick
}: CellProps) {
  return <button type="button" aria-label={name} aria-pressed={selected} disabled={disabled} onClick={onClick} {...stylex.props(styles.cell, cellHeights[breakpoint], selected && styles.picked, disabled && styles.cellOff, !disabled && press.scale)}>
      {label}
    </button>;
}
function MonthArrow({
  icon,
  label,
  disabled,
  onClick
}: ArrowProps) {
  return <button type="button" aria-label={label} disabled={disabled} onClick={onClick} {...stylex.props(styles.arrow, !disabled && press.scale)}>
      <Icon icon={icon} size={20} />
    </button>;
}
function useFocusAtEnd<T extends TextField>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const field = ref.current;
    if (!field) return;
    field.focus({
      preventScroll: true
    });
    field.setSelectionRange(field.value.length, field.value.length);
  }, []);
  return ref;
}
function enterIsNext(onAction: EventPickerProps["onAction"]) {
  return (event: KeyboardEvent<TextField>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    onAction({
      type: "next"
    });
  };
}
function headerLabel({
  breakpoint,
  word,
  draft
}: EventPickerProps): string {
  if (word !== "end" || breakpoint === "small" || draft.end === undefined) return fields[word];
  return words.endsLength(formatLength(draft.end - draft.start));
}
function hourCell(hour: number, clock: Clock) {
  if (clock === "12h") return {
    label: String(hour % 12 || 12),
    name: formatHour(hour * 60, clock)
  };
  return {
    label: pad(hour),
    name: words.hour24Cell(pad(hour))
  };
}
function meridiemOf(minutes: number): Meridiem {
  return minutes >= 12 * 60 ? "PM" : "AM";
}
function pad(n: number): string {
  return String(n).padStart(2, "0");
}
function isLaterSameWeekday(day: string, first: string): boolean {
  return day > first && parseDateKey(day).getUTCDay() === parseDateKey(first).getUTCDay();
}